import { LEAVES_BG_XS, LEAVES_BG_MD } from '../config/backgrounds';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  IconButton,
  Paper,
  Stack,
  Collapse,
  ListItemButton
} from '@mui/material';
import { 
  Person, 
  Business, 
  Edit, 
  Star, 
  Message, 
  Visibility,
  TrendingUp,
  Event,
  PhotoLibrary,
  CheckCircle,
  Pending,
  Cancel,
  Schedule,
  Favorite,
  BookOnline,
  CalendarMonth,
  Error,
  LocationOn,
  PushPin,
  Delete,
  Close,
  Send,
  Reply,
  CalendarToday,
  BarChart,
  ExpandMore,
  ExpandLess,
  Search,
  EventNote,
  Map as MapIcon,
  Lightbulb
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import dashboardService from '../services/dashboardService';
import ProviderOnboardingBanner from '../components/Provider/ProviderOnboardingBanner';
import { fetchProviderStatsSummary } from '../services/analyticsService';
import bookingService from '../services/bookingService';
import userPinService from '../services/userPinService';
import MapComponent from '../components/Map/MapComponent';
import ProviderCalendar from '../components/Provider/ProviderCalendar';
import BookingsList from '../components/Booking/BookingsList';
import FeaturedPromoCard from '../components/Featured/FeaturedPromoCard';
import { useConfirm } from '../components/Common/ConfirmDialog';


const Dashboard = () => {
  // --- AZ ÖSSZES HOOKOT ITT KELL DEKLARÁLNI, FELTÉTEL NÉLKÜL ---
  // MÓDOSÍTVA: `isAuthenticated` és `authLoading` eltávolítva a useAuth-ból.
  // A ProtectedRoute már garantálja, hogy a felhasználó be van jelentkezve,
  // mire a Dashboard renderelődni kezd.
  const { user, isProvider, isClient, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  
  const [loading, setLoading] = useState(true); // Helyi loading állapot a dashboard adatokhoz
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [adminStats, setAdminStats] = useState(null);

  const [mapCenter, setMapCenter] = useState({ lat: 47.4979, lng: 19.0402 });
  const [mapZoom, setMapZoom] = useState(7);
  const [mapMarkers, setMapMarkers] = useState([]);
  const [userPins, setUserPins] = useState([]);
  const [bookingsWithCoords, setBookingsWithCoords] = useState([]);
  const [mapBounds, setMapBounds] = useState(null);
  const [highlightMarkerId, setHighlightMarkerId] = useState(null);

  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  // Térkép: alapból csak akkor nyitva, ha van rajta jelölő; gombostűt csak "lerakás" módban teszünk le,
  // hogy görgetés / véletlen érintés ne hozzon létre újat
  const [mapOpen, setMapOpen] = useState(null);
  const [pinMode, setPinMode] = useState(false);
  const [tipsOpen, setTipsOpen] = useState(false);
  const [currentPin, setCurrentPin] = useState(null);
  const [pinForm, setPinForm] = useState({ note: '' });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [miniStats, setMiniStats] = useState(null);
  const [confirm, confirmDialog] = useConfirm();

  // Áttekintő sáv: teendők száma a foglalásokból (a lista minden frissülésekor újraszámoljuk)
  const [overviewBookings, setOverviewBookings] = useState(null);
  const [bookingsFilterRequest, setBookingsFilterRequest] = useState(null);
  const loadOverview = useCallback(async () => {
    try {
      const res = await bookingService.getBookings({ limit: 100 });
      if (res.success) setOverviewBookings(res.data || []);
    } catch {
      // az áttekintő sáv nem kritikus — hiba esetén egyszerűen nem jelenik meg
    }
  }, []);

  const overviewRef = useRef(null);
  const mapRef = useRef(null);
  const bookingsRef = useRef(null);
  const calendarRef = useRef(null);
  const adminRef = useRef(null);

  useEffect(() => {
    if (user && isAdmin()) {
      navigate('/admin', { replace: true });
    }
  }, [user, isAdmin, navigate]);

  // Mini statisztika widget betöltése (csak providereknek, 7 napos)
  useEffect(() => {
    if (!user || !isProvider()) return;
    const end = new Date();
    const start = new Date(end.getTime() - 6 * 24 * 60 * 60 * 1000);
    const fmt = (d) => d.toISOString().split('T')[0];
    fetchProviderStatsSummary(fmt(start), fmt(end))
      .then((res) => { if (res.success) setMiniStats(res.data); })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Scroll-oz a foglalások szekciójára ha az értesítésből érkeztünk
  useEffect(() => {
    if (location.state?.scrollToBooking && bookingsRef.current) {
      setTimeout(() => {
        bookingsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [location.state?.scrollToBooking]);

  // useEffect a dashboard adatok betöltéséhez
  // Itt már feltételezzük, hogy a felhasználó be van jelentkezve,
  // mivel a ProtectedRoute nem engedné renderelődni a komponenst máskülönben.
  // Az `user` objektumra való függőség továbbra is fontos, hogy az adatok betöltése
  // csak akkor történjen meg, ha a `user` objektum már elérhető.
  useEffect(() => {
    // MÓDOSÍTVA: `authLoading` és `isAuthenticated` ellenőrzések eltávolítva.
    // A ProtectedRoute már gondoskodott ezekről.
    if (user) {
      loadDashboardData();
      if (isClient()) {
        loadMapData();
      }
    }
  }, [user, isClient]); // MÓDOSÍTVA: `authLoading` és `isAuthenticated` eltávolítva a függőségekből

  // useEffect a térkép markerekhez (függ a bookingsWithCoords és userPins állapotoktól)
  useEffect(() => {
    const markers = [];

    bookingsWithCoords.forEach(booking => {
      markers.push({
        id: `booking-${booking.id}`,
        lat: booking.eventLatitude,
        lng: booking.eventLongitude,
        title: `Foglalás: ${booking.eventType}`,
        description: `${booking.provider?.businessName || 'Ismeretlen szolgáltató'} - ${new Date(booking.eventDate).toLocaleDateString('hu-HU')}`,
        type: 'booking',
        link: `/szolgaltato/${booking.providerId}`,
        icon: {
          url: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png',
          scaledSize: new window.google.maps.Size(32, 32)
        }
      });
    });

    userPins.forEach(pin => {
      markers.push({
        id: `pin-${pin.id}`,
        lat: pin.latitude,
        lng: pin.longitude,
        title: pin.note || 'Saját gombostű',
        description: `Létrehozva: ${new Date(pin.createdAt).toLocaleDateString('hu-HU')}`,
        type: 'userPin',
        pinId: pin.id,
        icon: {
          url: 'https://maps.google.com/mapfiles/ms/icons/yellow-dot.png',
          scaledSize: new window.google.maps.Size(32, 32)
        }
      });
    });

    setMapMarkers(markers);

    if (markers.length > 0) {
      let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
      markers.forEach(marker => {
        minLat = Math.min(minLat, marker.lat);
        maxLat = Math.max(maxLat, marker.lat);
        minLng = Math.min(minLng, marker.lng);
        maxLng = Math.max(maxLng, marker.lng);
      });

      const centerLat = (minLat + maxLat) / 2;
      const centerLng = (minLng + maxLng) / 2;
      // ~0.05 fok ≈ 5 km: ha ennél közelebb vannak egymáshoz (vagy csak 1 marker van),
      // fix zoom-ot használunk hogy legyen kontextus körülötte
      const MIN_SPREAD = 0.05;
      if ((maxLat - minLat) < MIN_SPREAD || (maxLng - minLng) < MIN_SPREAD) {
        setMapBounds(null);
        setMapCenter({ lat: centerLat, lng: centerLng });
        setMapZoom(11);
      } else {
        setMapBounds({
          north: maxLat,
          south: minLat,
          east: maxLng,
          west: minLng,
        });
        setMapCenter(null);
        setMapZoom(null);
      }
      setHighlightMarkerId(null);
    } else {
      setMapBounds({
        north: 48.58,
        south: 45.75,
        east: 22.95,
        west: 16.11,
      });
      setMapCenter({ lat: 47.4979, lng: 19.0402 });
      setMapZoom(7);
      setHighlightMarkerId(null);
    }
  }, [bookingsWithCoords, userPins]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const userType = user.userType || 'CLIENT';
      const response = await dashboardService.getDashboardData(userType);
      
      setDashboardData(response);

      if (isAdmin()) {
        try {
          const statsResponse = await dashboardService.getAdminStats();
          setAdminStats(statsResponse);
        } catch (error) {
          console.error('❌ Admin stats error:', error);
        }
      }
    } catch (error) {
      console.error('❌ Dashboard error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadMapData = async () => {
    try {
      const bookingsResponse = await bookingService.getBookings({ include: 'eventCoordinates', limit: 100 });
      const pinsResponse = await userPinService.getUserPins();

      if (bookingsResponse.success) {
        setBookingsWithCoords(bookingsResponse.data.filter(b => b.eventLatitude && b.eventLongitude));
      }
      if (pinsResponse.success) {
        setUserPins(pinsResponse.data.userPins);
      }
    } catch (err) {
      console.error('Térkép adatok betöltése hiba:', err);
      setError('Hiba a térkép adatok betöltése során.');
    }
  };


  // --- FELTÉTELES RENDERELÉS ITT KEZDŐDIK ---

  // A ProtectedRoute felelős az autentikáció betöltéséért és ellenőrzéséért.
  // Ha a Dashboard komponens idáig eljut, az azt jelenti, hogy a felhasználó
  // be van jelentkezve és az autentikáció ellenőrzése befejeződött.
  // Ezért az `authLoading` és `isAuthenticated` ellenőrzések már nem szükségesek itt.

  // 1. Dashboard adatok betöltési állapotának megjelenítése (helyi loading state)
  if (loading) { // Ez a `loading` állapot a dashboard adatok betöltésére vonatkozik
    return (
      <Container maxWidth={false} disableGutters sx={{ py: 4, px: { xs: 1, sm: 2 } }}>
        <Box sx={{ textAlign: 'center', width: '100%', maxWidth: { xs: '100%', md: 1440 }, mx: 'auto' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Dashboard betöltése...
          </Typography>
        </Box>
      </Container>
    );
  }

  const getProviderStatusLabel = (status) => {
    const labels = {
      DRAFT: 'Piszkozat',
      PENDING: 'Jóváhagyásra vár',
      APPROVED: 'Jóváhagyott',
      REJECTED: 'Elutasítva',
      SUSPENDED: 'Felfüggesztve',
    };
    return labels[status] || 'Függőben';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'OFFER_SENT': return 'info';
      case 'OFFER_REJECTED': return 'error';
      case 'APPROVED': return 'success';
      case 'REJECTED': return 'error';
      case 'COMPLETED': return 'default';
      case 'CANCELLED': return 'default';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'PENDING': return 'Ajánlatot kért';
      case 'OFFER_SENT': return 'Ajánlatot kapott';
      case 'OFFER_ACCEPTED': return 'Ajánlat elfogadva';
      case 'OFFER_REJECTED': return 'Ajánlat visszautasítva';
      case 'APPROVED': return 'Jóváhagyott foglalás';
      case 'REJECTED': return 'Elutasított foglalás';
      case 'COMPLETED': return 'Befejezett';
      case 'CANCELLED': return 'Lemondott';
      default: return status;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PENDING': return <Pending />;
      case 'OFFER_SENT': return <Send />;
      case 'OFFER_ACCEPTED': return <CheckCircle />;
      case 'OFFER_REJECTED': return <Cancel />;
      case 'APPROVED': return <CheckCircle />;
      case 'REJECTED': return <Cancel />;
      case 'COMPLETED': return <CheckCircle />;
      case 'CANCELLED': return <Cancel />;
      default: return <Schedule />;
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'BOOKING_CONFIRMED':
      case 'OFFER_ACCEPTED':
        return <CheckCircle />;
      case 'BOOKING_CANCELLED':
      case 'BOOKING_REJECTED':
      case 'BOOKING_CANCELLED_BY_CLIENT':
      case 'OFFER_REJECTED':
        return <Error />;
      case 'BOOKING_REMINDER':
        return <CalendarMonth />;
      case 'NEW_OFFER_REQUEST':
      case 'NEW_BOOKING':
        return <BookOnline />;
      case 'OFFER_RECEIVED':
        return <Send />;
      case 'NEW_OFFER_REQUEST_MESSAGE':
        return <Reply />;
      case 'NEW_REVIEW':
        return <Star />;
      case 'REVIEW_APPROVED':
        return <CheckCircle />;
      case 'REVIEW_REJECTED':
        return <Error />;
      case 'PROVIDER_APPROVED':
        return <CheckCircle />;
      case 'PROVIDER_REJECTED':
        return <Error />;
      default:
        return <Message />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'BOOKING_CONFIRMED':
      case 'PROVIDER_APPROVED':
      case 'REVIEW_APPROVED':
      case 'OFFER_ACCEPTED':
        return 'success';
      case 'BOOKING_CANCELLED':
      case 'BOOKING_REJECTED':
      case 'BOOKING_CANCELLED_BY_CLIENT':
      case 'PROVIDER_REJECTED':
      case 'REVIEW_REJECTED':
      case 'OFFER_REJECTED':
        return 'error';
      case 'BOOKING_REMINDER':
        return 'warning';
      case 'NEW_REVIEW':
      case 'NEW_BOOKING':
      case 'NEW_OFFER_REQUEST':
      case 'OFFER_RECEIVED':
      case 'NEW_OFFER_REQUEST_MESSAGE':
        return 'info';
      default:
        return 'default';
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleMapClick = (coords) => {
    if (!pinMode) return;
    setPinMode(false);
    setCurrentPin({ latitude: coords.lat, longitude: coords.lng, note: '' });
    setPinForm({ note: '' });
    setPinDialogOpen(true);
    setHighlightMarkerId(null);
  };

  const handleMapMarkerClick = (markerId) => {
    const marker = mapMarkers.find(m => m.id === markerId);
    if (marker && marker.type === 'userPin') {
      const pin = userPins.find(p => `pin-${p.id}` === markerId);
      if (pin) {
        setCurrentPin(pin);
        setPinForm({ note: pin.note || '' });
        setPinDialogOpen(true);
        setHighlightMarkerId(markerId);
      }
    } else if (marker && marker.type === 'booking') {
      navigate(marker.link);
    }
  };

  const handlePinListItemClick = (markerId, lat, lng) => {
    setMapBounds(null);
    setMapCenter({ lat, lng });
    setMapZoom(15);
    setHighlightMarkerId(markerId);
    mapRef.current?.scrollIntoView({ behavior: 'smooth' }); // Görgetés a térkép szekcióhoz
  };

  const handleSavePin = async () => {
    try {
      if (currentPin.id) {
        await userPinService.updateUserPin(currentPin.id, {
          latitude: currentPin.latitude,
          longitude: currentPin.longitude,
          note: pinForm.note
        });
        showSnackbar('Gombostű sikeresen frissítve!', 'success');
      } else {
        await userPinService.createUserPin({
          latitude: currentPin.latitude,
          longitude: currentPin.longitude,
          note: pinForm.note
        });
        showSnackbar('Gombostű sikeresen létrehozva!', 'success');
      }
      setPinDialogOpen(false);
      setCurrentPin(null);
      setPinForm({ note: '' });
      loadMapData();
      setHighlightMarkerId(null);
    } catch (err) {
      console.error('Gombostű mentése hiba:', err);
      showSnackbar('Hiba a gombostű mentése során.', 'error');
    }
  };

  const handleDeletePin = async () => {
    if (!(await confirm({ title: 'Gombostű törlése', message: 'Biztosan törli ezt a gombostűt? A megjegyzése is elvész.', confirmLabel: 'Törlés', color: 'error', icon: <Delete /> }))) {
      return;
    }
    try {
      if (currentPin && currentPin.id) {
        await userPinService.deleteUserPin(currentPin.id);
        showSnackbar('Gombostű sikeresen törölve!', 'success');
        setPinDialogOpen(false);
        setCurrentPin(null);
        setPinForm({ note: '' });
        loadMapData();
        setHighlightMarkerId(null);
      }
    } catch (err) {
      console.error('Gombostű törlése hiba:', err);
      showSnackbar('Hiba a gombostű törlése során.', 'error');
    }
  };

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };



  // Áttekintő sáv elemei — kattintásra a foglaláslista az adott státuszra szűr
  const now = new Date();
  const in30Days = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  const PAYABLE = ['NOT_PAID', 'DEPOSIT_REQUESTED', 'DEPOSIT_PAID'];
  const countWhere = (pred) => (overviewBookings || []).filter(pred).length;
  const upcomingCount = countWhere(b => b.status === 'APPROVED' && new Date(b.eventDate) >= now && new Date(b.eventDate) <= in30Days);
  const overviewItems = isProvider()
    ? [
        { key: 'needsOffer', label: 'ajánlatkérés vár válaszra', count: countWhere(b => b.status === 'PENDING' || b.status === 'OFFER_REJECTED'), status: 'PENDING', icon: <Pending />, urgent: true },
        { key: 'toApprove', label: 'jóváhagyásra vár', count: countWhere(b => b.status === 'OFFER_ACCEPTED'), status: 'OFFER_ACCEPTED', icon: <CheckCircle />, urgent: true },
        { key: 'upcoming', label: 'esemény 30 napon belül', count: upcomingCount, status: 'APPROVED', icon: <CalendarMonth /> },
      ]
    : [
        { key: 'offers', label: 'ajánlat vár döntésre', count: countWhere(b => b.status === 'OFFER_SENT'), status: 'OFFER_SENT', icon: <Send />, urgent: true },
        { key: 'toPay', label: 'fizetésre vár', count: countWhere(b => b.status === 'APPROVED' && PAYABLE.includes(b.paymentStatus) && new Date(b.eventDate) >= now), status: 'APPROVED', icon: <Schedule />, urgent: true },
        { key: 'upcoming', label: 'esemény 30 napon belül', count: upcomingCount, status: 'APPROVED', icon: <CalendarMonth /> },
      ];
  const mapOpenEffective = mapOpen ?? (mapMarkers.length > 0);

  // Gyorsműveletek — mobilon a lista fölött, desktopon az oldalsávban
  const quickActions = isProvider()
    ? [
        { label: 'Profilom', to: '/profil/szolgaltato', icon: <Edit />, primary: true },
        { label: 'Galéria', to: '/profil/szolgaltato?tab=2', icon: <PhotoLibrary /> },
        { label: 'Üzenetek', to: '/messages', icon: <Message /> },
        { label: 'Statisztika', to: '/statisztika', icon: <BarChart /> },
      ]
    : [
        { label: 'Esküvőtervező', to: '/wedding-planner', icon: <EventNote />, primary: true },
        { label: 'Szolgáltatók', to: '/szolgaltatok', icon: <Search /> },
        { label: 'Kedvencek', to: '/kedvencek', icon: <Favorite /> },
        { label: 'Üzenetek', to: '/messages', icon: <Message /> },
      ];

  const handleOverviewClick = (item) => {
    setBookingsFilterRequest({ status: item.status, key: Date.now() });
    scrollToSection(bookingsRef);
  };

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        minHeight: '100vh',
        pt: { xs: 3, md: 4 },
        pb: { xs: 12, md: 4 },
        px: { xs: 2, sm: 3 },
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'fixed',
          inset: 0,
          backgroundImage: {
            xs: LEAVES_BG_XS,
            md: LEAVES_BG_MD,
          },
          backgroundSize: {
            xs: '100% auto',
            md: 'cover',
          },
          backgroundPosition: {
            xs: 'center top',
            md: 'center center',
          },
          backgroundRepeat: 'no-repeat',
          zIndex: -1,
          willChange: 'transform',
          transform: 'translateZ(0)',
        },
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: {
            xs: '100%',
            md: 1440, // Maximális szélesség nagyobb képernyőkön
          },
          mx: 'auto', // Középre igazítás
        }}
      >

        {/* Üdvözlő szekció */}
        <Paper
          elevation={0}
          sx={{
            mb: { xs: 2, md: 3 },
            p: { xs: 2.5, md: 4 },
            borderRadius: '20px',
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.92)} 0%, ${alpha(theme.palette.secondary.light, 0.88)} 100%)`,
            backdropFilter: 'blur(12px)',
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.30)}`,
            boxShadow: `0 4px 24px ${alpha(theme.palette.primary.dark, 0.12)}`,
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ color: 'primary.dark', fontSize: { xs: '1.5rem', md: '1.75rem' } }}>
            Üdvözöljük, {user?.firstName}!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {isAdmin() && 'Admin Dashboard'}
            {isProvider() && `${user?.provider?.businessName || 'Szolgáltató'} — szolgáltatói felület`}
            {isClient() && 'Itt követheti nyomon ajánlatkéréseit, foglalásait és kedvenc szolgáltatóit.'}
          </Typography>
        </Paper>

        {/* Áttekintő sáv: a legfontosabb teendők számlálóval, kattintásra szűrt lista */}
        {/* Ha nincs semmi teendő, három nulla helyett egyetlen nyugtázó sor */}
        {(isProvider() || isClient()) && overviewBookings && overviewItems.every(i => i.count === 0) && (
          <Paper
            elevation={0}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5, mb: 4, px: 2, py: 1.5,
              borderRadius: '16px',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              bgcolor: alpha(theme.palette.background.paper, 0.9),
            }}
          >
            <CheckCircle sx={{ color: 'success.main' }} />
            <Typography variant="body2" color="text.secondary">
              Nincs függő teendő — {isProvider() ? 'minden ajánlatkérésre válaszolt.' : 'nincs döntésre vagy fizetésre váró foglalása.'}
            </Typography>
          </Paper>
        )}
        {(isProvider() || isClient()) && overviewBookings && overviewItems.some(i => i.count > 0) && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: { xs: 1, sm: 2 },
              mb: 4,
            }}
          >
            {overviewItems.map((item) => {
              const active = item.count > 0;
              const highlight = active && item.urgent;
              return (
                <Paper
                  key={item.key}
                  component={active ? 'button' : 'div'}
                  type={active ? 'button' : undefined}
                  onClick={active ? () => handleOverviewClick(item) : undefined}
                  elevation={0}
                  sx={{
                    display: 'flex',
                    // Telefonon három keskeny oszlop: szám felül, rövid felirat alatta, ikon nélkül
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { xs: 'flex-start', md: 'center' },
                    gap: { xs: 0.25, md: 1.5 },
                    p: { xs: 1.25, sm: 2 },
                    width: '100%',
                    textAlign: 'left',
                    font: 'inherit',
                    cursor: active ? 'pointer' : 'default',
                    borderRadius: '16px',
                    border: '1px solid',
                    borderColor: highlight ? alpha(theme.palette.secondary.main, 0.8) : alpha(theme.palette.primary.main, 0.15),
                    bgcolor: highlight ? alpha(theme.palette.secondary.main, 0.16) : alpha(theme.palette.background.paper, 0.9),
                    transition: 'box-shadow 0.15s, transform 0.15s',
                    '&:hover': active ? { boxShadow: `0 4px 14px ${alpha(theme.palette.primary.dark, 0.12)}`, transform: 'translateY(-1px)' } : undefined,
                    '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
                  }}
                >
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '12px', flexShrink: 0,
                    display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'center',
                    bgcolor: active ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.08),
                    color: active ? theme.palette.common.white : theme.palette.text.secondary,
                  }}>
                    {item.icon}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h5" component="div" sx={{ lineHeight: 1.1, color: active ? 'primary.dark' : 'text.secondary', fontWeight: 700 }}>
                      {item.count}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.3, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {item.label}
                    </Typography>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}

        {/* Onboarding banner – csak PENDING státuszú szolgáltatóknak */}
        {isProvider() && user?.provider?.status === 'PENDING' && (
          <ProviderOnboardingBanner providerId={user.provider.id} />
        )}

        {/* Hiba megjelenítése */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
          action={
            <IconButton size="small" aria-label="close" color="inherit" onClick={() => setSnackbarOpen(false)}>
              <Close fontSize="small" />
            </IconButton>
          }
        >
          <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>


        {/* Fő Grid konténer a két oszlopos elrendezéshez */}
        <Grid container spacing={4}>
          {/* Bal oldal - Fő tartalom */}
          <Grid size={{ xs: 12, md: isProvider() || isClient() ? 8 : 12 }}>
            {/* Gyorsműveletek mobilon / tableten — desktopon az oldalsávban vannak */}
            {(isProvider() || isClient()) && (
              <Box
                sx={{
                  display: { xs: 'grid', md: 'none' },
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 1,
                  mb: 3,
                }}
              >
                {quickActions.map((action) => (
                  <Button
                    key={action.label}
                    variant={action.primary ? 'contained' : 'outlined'}
                    startIcon={action.icon}
                    onClick={() => navigate(action.to)}
                    sx={{
                      minHeight: 44,
                      justifyContent: 'flex-start',
                      bgcolor: action.primary ? undefined : alpha(theme.palette.background.paper, 0.85),
                    }}
                  >
                    {action.label}
                  </Button>
                ))}
              </Box>
            )}

            {/* Szekció-ugrók (desktop) */}
            <Stack
              direction="row"
              spacing={1}
              sx={{
                display: { xs: 'none', md: 'flex' },
                mb: 3,
                flexWrap: 'wrap',
                gap: 1,
                '& .MuiButton-outlined': {
                  borderRadius: '20px',
                  borderColor: alpha(theme.palette.primary.main, 0.50),
                  color: theme.palette.primary.dark,
                  background: alpha(theme.palette.background.paper, 0.70),
                  backdropFilter: 'blur(8px)',
                  '&:hover': {
                    borderColor: theme.palette.secondary.main,
                    color: theme.palette.primary.dark,
                    background: alpha(theme.palette.background.paper, 0.92),
                    boxShadow: `0 2px 8px ${alpha(theme.palette.secondary.main, 0.30)}`,
                  },
                },
              }}
            >
              {(isProvider() || isClient()) && <Button variant="outlined" onClick={() => scrollToSection(bookingsRef)}>Ajánlatkérések és foglalások</Button>}
              {isClient() && <Button variant="outlined" onClick={() => { setMapOpen(true); scrollToSection(mapRef); }}>Térkép</Button>}
              {isProvider() && <Button variant="outlined" onClick={() => scrollToSection(calendarRef)}>Naptár</Button>}
              {isAdmin() && <Button variant="outlined" onClick={() => scrollToSection(adminRef)}>Admin panel</Button>}
            </Stack>

            {/* Ajánlatkérések és Foglalások szekció (PROVIDER és CLIENT számára is) - FELÜLRE HELYEZVE */}
            {(isProvider() || isClient()) && (
              <Box id="bookings-section" ref={bookingsRef} sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                  <Box sx={{ width: 4, height: 28, borderRadius: 1, bgcolor: 'secondary.main', flexShrink: 0 }} />
                  <Typography variant="h5" sx={{ color: 'primary.dark', fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                    {isClient() ? 'Ajánlatkéréseim és foglalásaim' : 'Ajánlatkérések és foglalások'}
                  </Typography>
                </Box>
                <BookingsList
                  isDashboardView={true}
                  statusFilterRequest={bookingsFilterRequest}
                  onBookingsChanged={loadOverview}
                />
              </Box>
            )}

            {/* Térkép szekció (csak CLIENT-nek) */}
            {isClient() && (
              <Box id="map-section" ref={mapRef} sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: mapOpenEffective ? 3 : 0 }}>
                  <Box sx={{ width: 4, height: 28, borderRadius: 1, bgcolor: 'secondary.main', flexShrink: 0 }} />
                  <Typography variant="h5" sx={{ color: 'primary.dark', fontSize: { xs: '1.25rem', md: '1.5rem' }, flex: 1, minWidth: 0 }}>
                    Saját térképem
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => { setMapOpen(!mapOpenEffective); setPinMode(false); }}
                    endIcon={mapOpenEffective ? <ExpandLess /> : <ExpandMore />}
                    aria-expanded={mapOpenEffective}
                    sx={{ flexShrink: 0 }}
                  >
                    {mapOpenEffective ? 'Elrejtés' : 'Megjelenítés'}
                  </Button>
                </Box>
                {!mapOpenEffective && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 2.5 }}>
                    Jelölje meg a fontos helyszíneket (templom, szertartás, szállás) — a foglalásai is itt jelennek meg.
                  </Typography>
                )}
                <Collapse in={mapOpenEffective} unmountOnExit>
                <Paper elevation={0} sx={{
                  mb: 3,
                  p: 0,
                  borderRadius: '20px',
                  overflow: 'hidden',
                  background: 'transparent',
                  boxShadow: `0 2px 16px ${alpha(theme.palette.primary.dark, 0.08)}`,
                }}>
                  <MapComponent
                    center={mapCenter}
                    zoom={mapZoom}
                    markers={mapMarkers}
                    onMapClick={handleMapClick}
                    onMarkerClick={handleMapMarkerClick}
                    mapBounds={mapBounds}
                    highlightMarkerId={highlightMarkerId}
                    options={{
                      // Mobilon egy ujjal görget az oldal, két ujjal mozog a térkép
                      gestureHandling: 'cooperative',
                      draggableCursor: pinMode ? 'crosshair' : undefined,
                    }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', px: 2, py: 1.5 }}>
                    <Button
                      size="small"
                      variant={pinMode ? 'contained' : 'outlined'}
                      startIcon={<PushPin />}
                      onClick={() => setPinMode(m => !m)}
                      sx={{ minHeight: 40 }}
                    >
                      {pinMode ? 'Mégse' : 'Gombostű lerakása'}
                    </Button>
                    <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 200 }}>
                      {pinMode
                        ? 'Koppintson a térképre oda, ahová a gombostűt szeretné.'
                        : 'Egy meglévő jelölőre koppintva megnézheti vagy szerkesztheti.'}
                    </Typography>
                  </Box>
                </Paper>

                {/* UserPin lista (opcionális) */}
                {(userPins.length > 0 || bookingsWithCoords.length > 0) && (
                  <Card sx={{
                    mt: 3,
                    borderRadius: '16px',
                    background: alpha(theme.palette.background.paper, 0.92),
                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.20)}`,
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.dark, 0.08)}`,
                  }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Gombostűim és Foglalásaim
                      </Typography>
                      <List dense>
                        {userPins.map(pin => (
                          <ListItem 
                            key={`pin-${pin.id}`}
                            button
                            onClick={() => handlePinListItemClick(`pin-${pin.id}`, pin.latitude, pin.longitude)}
                            secondaryAction={
                              <IconButton edge="end" aria-label="edit" onClick={() => handleMapMarkerClick(`pin-${pin.id}`)}>
                                <Edit />
                              </IconButton>
                            }
                          >
                            <ListItemIcon>
                              <PushPin />
                            </ListItemIcon>
                            <ListItemText
                              primary={pin.note || `Gombostű (${pin.latitude.toFixed(4)}, ${pin.longitude.toFixed(4)})`}
                              secondary={`Létrehozva: ${new Date(pin.createdAt).toLocaleDateString('hu-HU')}`}
                            />
                          </ListItem>
                        ))}
                        {bookingsWithCoords.map(booking => (
                          <ListItem 
                            key={`booking-${booking.id}`}
                            button
                            onClick={() => handlePinListItemClick(`booking-${booking.id}`, booking.eventLatitude, booking.eventLongitude)}
                          >
                            <ListItemIcon>
                              <Event />
                            </ListItemIcon>
                            <ListItemText
                              primary={`Foglalás: ${booking.eventType}`}
                              secondary={`${booking.provider?.businessName || 'Ismeretlen szolgáltató'} - ${new Date(booking.eventDate).toLocaleDateString('hu-HU')}`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                )}
                </Collapse>
              </Box>
            )}

            {/* Naptár szekció (csak PROVIDER-nek) */}
            {isProvider() && (
              <Box id="calendar-section" ref={calendarRef} sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                  <Box sx={{ width: 4, height: 28, borderRadius: 1, bgcolor: 'secondary.main', flexShrink: 0 }} />
                  <Typography variant="h5" sx={{ color: 'primary.dark', fontSize: { xs: '1.25rem', md: '1.5rem' } }}>Foglalási naptár</Typography>
                </Box>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, md: 3 },
                    borderRadius: '20px',
                    background: `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.96)} 0%, ${alpha(theme.palette.secondary.light, 0.22)} 100%)`,
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.22)}`,
                    boxShadow: `0 4px 24px ${alpha(theme.palette.primary.dark, 0.10)}, inset 0 1px 0 ${alpha('#fff', 0.70)}`,
                  }}
                >
                  <ProviderCalendar />
                </Paper>
              </Box>
            )}

            {/* Áttekintés szekció (csak Admin tippekkel, ha admin) - ALULRA HELYEZVE */}
            {isAdmin() && (
              <Box id="overview-section" ref={overviewRef} sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                  <Box sx={{ width: 4, height: 28, borderRadius: 1, bgcolor: 'secondary.main', flexShrink: 0 }} />
                  <Typography variant="h5" sx={{ color: 'primary.dark' }}>Áttekintés</Typography>
                </Box>
                <Grid container spacing={3}>
                  <Grid size={12}>
                    <Card sx={{
                      borderRadius: '16px',
                      background: alpha(theme.palette.background.paper, 0.92),
                      backdropFilter: 'blur(10px)',
                      border: `1px solid ${alpha(theme.palette.secondary.main, 0.20)}`,
                      boxShadow: `0 4px 16px ${alpha(theme.palette.primary.dark, 0.08)}`,
                    }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Admin tippek
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            🔧 <strong>Rendszer:</strong> Rendszeresen ellenőrizze a függőben lévő jóváhagyásokat
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            📊 <strong>Statisztikák:</strong> Kövesse nyomon a platform növekedését
                          </Typography>
                          <Typography variant="body2">
                            👥 <strong>Felhasználók:</strong> Segítse a felhasználókat problémák esetén
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Admin panel szekció (csak ADMIN-nak) */}
            {isAdmin() && (
              <Box id="admin-section" ref={adminRef} sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                  <Box sx={{ width: 4, height: 28, borderRadius: 1, bgcolor: 'secondary.main', flexShrink: 0 }} />
                  <Typography variant="h5" sx={{ color: 'primary.dark' }}>Admin panel</Typography>
                </Box>
                <Typography>Adminisztrációs felület.</Typography>
              </Box>
            )}
          </Grid>

          {/* Jobb oldal - Sticky Sidebar */}
          {(isProvider() || isClient()) && (
            <Grid size={{ xs: 12, md: 4 }}>
            <Stack spacing={3}> {/* MÓDOSÍTVA: Card helyett Stack, hogy több widget fér el */}
              {/* Profil kártya */}
              <Card sx={{
                borderRadius: '20px',
                background: alpha(theme.palette.background.paper, 0.95),
                backdropFilter: 'blur(12px)',
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.28)}`,
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.dark, 0.15)}`,
                overflow: 'hidden',
              }}>
                <CardContent>
                  {/* Profil kártya */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      src={user?.avatarUrl || undefined}
                      sx={{
                        mr: 2,
                        width: 52,
                        height: 52,
                        bgcolor: isProvider() ? 'secondary.main' : 'primary.main',
                        color: isProvider() ? 'primary.dark' : 'common.white',
                        border: `3px solid`,
                        borderColor: isProvider() ? 'secondary.main' : 'primary.light',
                        boxShadow: `0 0 0 3px ${alpha(isProvider() ? theme.palette.secondary.main : theme.palette.primary.main, 0.20)}`,
                      }}
                    >
                      {!user?.avatarUrl && (isProvider() ? <Business /> : <Person />)}
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {isProvider() ? (user?.provider?.businessName || `${user?.firstName} ${user?.lastName}`) : `${user?.firstName} ${user?.lastName}`}
                      </Typography>
                      <Chip 
                        icon={isProvider() ? <Business /> : <Person />} 
                        label={isProvider() ? 'Szolgáltató' : 'Ügyfél'} 
                        size="small" 
                        color={isProvider() ? 'secondary' : 'primary'} 
                      />
                    </Box>
                  </Box>
                  
                  <Typography variant="body2" gutterBottom>
                    <strong>Email:</strong> {user?.email}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Telefon:</strong> {user?.phone || 'Nincs megadva'}
                  </Typography>
                  {isProvider() && (
                    <>
                      <Typography variant="body2" gutterBottom>
                        <strong>Város:</strong> {user?.provider?.city || 'Nincs megadva'}
                      </Typography>
                      <Typography variant="body2" component="div" gutterBottom>
                        <strong>Státusz:</strong>
                        <Chip
                          label={getProviderStatusLabel(user?.provider?.status)}
                          size="small"
                          color={getStatusColor(user?.provider?.status)}
                          sx={{ ml: 1 }}
                        />
                      </Typography>
                    </>
                  )}

                  <Grid container spacing={1} sx={{ mt: 2, display: { xs: 'none', md: 'flex' } }}>
                    {/* Klienseknek teljes szélesség, providereknél a blokkba kerül */}
                    {!isProvider() && (
                      <Grid size={12}>
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<Edit />}
                          onClick={() => navigate('/profil')}
                        >
                          Profil szerkesztése
                        </Button>
                      </Grid>
                    )}

                    {/* Szolgáltatói gyorsműveletek */}
                    {isProvider() && (
                      <>
                        <Grid size={6}>
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<Edit />}
                            size="small"
                            onClick={() => navigate('/profil/szolgaltato')}
                          >
                            Szerkesztés
                          </Button>
                        </Grid>
                        <Grid size={6}>
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<PhotoLibrary />}
                            size="small"
                            onClick={() => navigate('/profil/szolgaltato?tab=2')}
                          >
                            Galéria
                          </Button>
                        </Grid>
                        <Grid size={6}>
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<Visibility />}
                            size="small"
                            onClick={() => navigate(`/szolgaltato/${user?.provider?.slug || user?.provider?.id}`)}
                          >
                            Profil
                          </Button>
                        </Grid>
                        <Grid size={6}>
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<Message />}
                            size="small"
                            onClick={() => navigate('/messages')}
                          >
                            Üzenetek
                          </Button>
                        </Grid>
                      </>
                    )}
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  {/* Ügyfél gyors műveletek (desktop; mobilon a lista fölött) */}
                  {isClient() && !isProvider() && (
                    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                      <Typography variant="h6" gutterBottom sx={{ color: 'primary.dark', fontWeight: 700 }}>
                        Gyors műveletek
                      </Typography>
                      <Stack spacing={1}>
                        <Button fullWidth variant="contained" startIcon={<EventNote />} onClick={() => navigate('/wedding-planner')}>
                          Esküvőtervező
                        </Button>
                        <Button fullWidth variant="outlined" startIcon={<Search />} onClick={() => navigate('/szolgaltatok')}>
                          Szolgáltató keresése
                        </Button>
                        <Button fullWidth variant="outlined" startIcon={<Message />} onClick={() => navigate('/messages')}>
                          Üzenetek
                        </Button>
                      </Stack>
                      <Divider sx={{ my: 3 }} />
                    </Box>
                  )}

                  {/* Kedvencek (csak CLIENT) */}
                  {isClient() && (
                    <>
                      <Typography variant="h6" gutterBottom sx={{ color: 'primary.dark', fontWeight: 700 }}>
                        Kedvencek
                      </Typography>
                      {dashboardData?.favorites?.recentFavorites?.length > 0 ? (
                        <List dense>
                          {dashboardData.favorites.recentFavorites.slice(0, 3).map((favorite) => (
                            <ListItemButton
                              key={favorite.id}
                              divider
                              onClick={() => navigate(`/szolgaltato/${favorite.provider.slug || favorite.provider.id}`)}
                              sx={{ borderRadius: '8px', px: 1 }}
                            >
                              <ListItemIcon sx={{ minWidth: 40 }}>
                                <Business sx={{ color: 'primary.main' }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={favorite.provider.businessName}
                                secondary={
                                  <Box>
                                    <Typography variant="body2" color="text.secondary">
                                      {favorite.provider.category?.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {favorite.provider.city}
                                    </Typography>
                                  </Box>
                                }
                              />
                            </ListItemButton>
                          ))}
                        </List>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                          Még nincsenek kedvenc szolgáltatók
                        </Typography>
                      )}
                      <Button 
                        fullWidth 
                        variant="outlined" 
                        startIcon={<Favorite />}
                        sx={{ mt: 2 }}
                        onClick={() => navigate('/kedvencek')}
                      >
                        Összes kedvenc
                      </Button>
                      <Divider sx={{ my: 3 }} />
                    </>
                  )}
                  
                  {/* Tippek: alapból összecsukva, hogy ne foglaljanak helyet */}
                  <Button
                    fullWidth
                    onClick={() => setTipsOpen(o => !o)}
                    startIcon={<Lightbulb />}
                    endIcon={tipsOpen ? <ExpandLess /> : <ExpandMore />}
                    aria-expanded={tipsOpen}
                    sx={{ justifyContent: 'flex-start', color: 'text.secondary', '& .MuiButton-endIcon': { ml: 'auto' } }}
                  >
                    Hasznos tippek
                  </Button>
                  <Collapse in={tipsOpen}>
                    <Box sx={{ pt: 1, px: 1 }}>
                      {(isProvider()
                        ? ['Gyorsan válaszoljon az ajánlatkérésekre — az ügyfelek több szolgáltatót is megkeresnek.', 'Tartsa naprakészen a naptárát, hogy ne kapjon foglalt napra kérést.', 'Kérjen értékelést az esemény után: a jó értékelés előrébb hozza a profilját.']
                        : ['Kérjen ajánlatot több szolgáltatótól is, hogy össze tudja hasonlítani őket.', 'Az üzenetekben minden részletet egy helyen tart a szolgáltatóval.', 'Az esemény után írjon értékelést — ezzel másoknak is segít.']
                      ).map(t => (
                        <Typography key={t} variant="body2" color="text.secondary" sx={{ mb: 1 }}>• {t}</Typography>
                      ))}
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>

              {/* Mini statisztika widget (csak providereknek) */}
              {isProvider() && miniStats && (
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: '20px',
                    background: alpha(theme.palette.background.paper, 0.95),
                    backdropFilter: 'blur(12px)',
                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.28)}`,
                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.dark, 0.15)}`,
                    overflow: 'hidden',
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BarChart sx={{ color: 'primary.main', fontSize: 20 }} />
                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                          Elmúlt 7 nap statisztikái
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => navigate('/statisztika')}
                        sx={{ borderRadius: '10px', minWidth: 0 }}
                      >
                        Statisztikák
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                      {[
                        // Arculati színek — korábban az ajánlatkérés piros volt, ami hibát sugallt
                        { label: 'Profil megtekintés', key: 'PROFILE_VIEW', color: theme.palette.primary.main },
                        { label: 'Gyors megtekintés', key: 'QUICK_VIEW_OPEN', color: theme.palette.primary.light },
                        { label: 'Ajánlatkérés', key: 'OFFER_REQUEST', color: theme.palette.warning.main },
                      ].map((item, idx) => (
                        <React.Fragment key={item.key}>
                          {idx > 0 && (
                            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                          )}
                          <Box sx={{ flex: 1, textAlign: 'center', py: 0.5 }}>
                            <Typography variant="h5" fontWeight={700} sx={{ color: item.color }}>
                              {(miniStats.totals?.[item.key] ?? 0).toLocaleString('hu-HU')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>
                              {item.label}
                            </Typography>
                          </Box>
                        </React.Fragment>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}
              {/* Kiemelés widget (a profil és a statisztika után): aktív kiemelésnél státusz, egyébként ajánló (csak jóváhagyott profilnak) */}
              {isProvider() && user?.provider?.categoryId && user?.provider?.status === 'APPROVED' && (
                <FeaturedPromoCard
                  providerId={user.provider.id}
                  categoryId={user.provider.categoryId}
                />
              )}
            </Stack> {/* MÓDOSÍTVA: Stack vége */}
          </Grid>
        )}
      </Grid>

        {confirmDialog}

        {/* UserPin Dialog (létrehozás/szerkesztés) */}
        <Dialog open={pinDialogOpen} onClose={() => setPinDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {currentPin?.id ? 'Gombostű szerkesztése' : 'Új gombostű elhelyezése'}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Koordináták: {currentPin?.latitude?.toFixed(4)}, {currentPin?.longitude?.toFixed(4)}
            </Typography>
            <TextField
              fullWidth
              label="Megjegyzés"
              multiline
              rows={3}
              margin="normal"
              value={pinForm.note}
              onChange={(e) => setPinForm({ ...pinForm, note: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            {currentPin?.id && (
              <Button onClick={handleDeletePin} color="error" startIcon={<Delete />}>
                Törlés
              </Button>
            )}
            <Button onClick={() => setPinDialogOpen(false)}>Mégse</Button>
            <Button onClick={handleSavePin} variant="contained" color="primary">
              Mentés
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default Dashboard;